import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { BoardPost, BoardPostComment, boardPostAudienceLabels } from '../../types/board';
import { boardAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, isGuestOnly } from '../../types/auth';

interface BoardPostCardProps {
  post: BoardPost;
  /** Az értesítésből ide érkeztünk: emeljük ki. */
  highlighted?: boolean;
  onEdit: (post: BoardPost) => void;
  onDelete: (post: BoardPost) => void;
  onChanged: () => void;
}

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const BoardPostCard: React.FC<BoardPostCardProps> = ({ post, highlighted = false, onEdit, onDelete, onChanged }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<BoardPostComment[] | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [readers, setReaders] = useState<string[] | null>(null);

  const audience = boardPostAudienceLabels(post);
  // A vendég olvasásra kapja a neki címzett bejegyzéseket - a szerver sem
  // engedné a hozzászólását, ezért a beviteli mezőt sem mutatjuk.
  const readOnly = isGuestOnly(user);

  const loadComments = async () => {
    try {
      setComments(await boardAPI.getComments(post.id));
    } catch (error) {
      console.error('A hozzászólások betöltése nem sikerült:', error);
    }
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      await loadComments();
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setBusy(true);
    try {
      await boardAPI.addComment(post.id, newComment.trim());
      setNewComment('');
      await loadComments();
      onChanged();
    } catch (error) {
      console.error('A hozzászólás mentése nem sikerült:', error);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    setBusy(true);
    try {
      await boardAPI.deleteComment(commentId);
      await loadComments();
      onChanged();
    } catch (error) {
      console.error('A hozzászólás törlése nem sikerült:', error);
    } finally {
      setBusy(false);
    }
  };

  const loadReaders = async () => {
    try {
      const list = await boardAPI.getReaders(post.id);
      setReaders(list.map((r) => r.name));
    } catch (error) {
      console.error('Az olvasók betöltése nem sikerült:', error);
    }
  };

  const canDeleteComment = (comment: BoardPostComment) =>
    comment.userId === user?.id || isAdmin(user);

  return (
    <article
      id={`board-post-${post.id}`}
      className={`rounded-lg border bg-white p-4 shadow-sm ${
        highlighted
          ? 'border-primary-500 ring-2 ring-primary-300'
          : post.isPinned
          ? 'border-primary-300 ring-1 ring-primary-100'
          : 'border-gray-200'
      } ${post.isExpired ? 'opacity-60' : ''}`}
    >
      {/* Fejléc: címzettek badge-ként, ahogy az eseményeknél a korosztály */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {post.isPinned && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-2 py-0.5 text-xs font-medium text-white">
            <MapPinIcon className="h-3.5 w-3.5" />
            Kitűzve
          </span>
        )}
        {audience.map((label) => (
          <span
            key={label}
            className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800"
          >
            {label}
          </span>
        ))}
        {post.isExpired && (
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
            Lejárt
          </span>
        )}
        {!post.isReadByMe && (
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Új
          </span>
        )}
      </div>

      <p className="whitespace-pre-wrap break-words hyphens-auto text-sm leading-relaxed text-gray-900">
        {post.content}
      </p>

      {/* Ha eseményhez kapcsolódik, egy kattintással oda lehet menni */}
      {post.event && (
        <Link
          to={`/events/${post.event.id}`}
          className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-800 hover:bg-primary-100"
        >
          <CalendarDaysIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {post.event.title} ·{' '}
            {new Date(post.event.startTime).toLocaleString('hu-HU', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </Link>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="font-medium text-gray-700">{post.authorName}</span>
        <span>{formatDateTime(post.createdAt)}</span>
        {post.updatedAt && <span>(szerkesztve)</span>}
        {post.expiresAt && !post.isExpired && (
          <span>Eddig: {new Date(post.expiresAt).toLocaleDateString('hu-HU')}</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleComments}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
          >
            <ChatBubbleLeftIcon className="h-4 w-4" />
            {post.commentCount > 0 ? `${post.commentCount} hozzászólás` : 'Hozzászólás'}
          </button>

          {/* Az olvasottság a szerzőnek szóló visszajelzés */}
          {post.readCount != null && (
            <button
              type="button"
              onClick={loadReaders}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
            >
              <EyeIcon className="h-4 w-4" />
              {post.readCount} olvasta
            </button>
          )}
        </div>

        {post.canModify && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(post)}
              aria-label="Szerkesztés"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-primary-600"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(post)}
              aria-label="Törlés"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {readers && (
        <p className="mt-2 text-xs text-gray-500">
          {readers.length > 0 ? `Olvasta: ${readers.join(', ')}` : 'Még senki nem olvasta.'}
        </p>
      )}

      {showComments && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
          {comments === null ? (
            <p className="text-sm text-gray-500">Betöltés...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-gray-500">Még nincs hozzászólás.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{comment.userName}</span>
                    {' · '}
                    {formatDateTime(comment.createdAt)}
                  </div>
                  <p className="whitespace-pre-wrap break-words hyphens-auto text-sm text-gray-800">
                    {comment.content}
                  </p>
                </div>
                {canDeleteComment(comment) && (
                  <button
                    type="button"
                    onClick={() => handleDeleteComment(comment.id)}
                    aria-label="Hozzászólás törlése"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          )}

          {/* A régebbi backend még nem küld canComment-et: akkor a bejegyzés kapcsolója dönt */}
          {(post.canComment ?? post.commentsEnabled) && !readOnly ? (
            <form onSubmit={handleAddComment} className="flex items-start gap-2">
              <textarea
                rows={2}
                maxLength={2000}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Írj hozzászólást..."
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={busy || !newComment.trim()}
                aria-label="Küldés"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <p className="text-sm text-gray-500">
              {readOnly
                ? 'Ehhez a bejegyzéshez csak olvasási jogod van.'
                : post.commentsEnabled
                  ? 'A klub beállítása szerint nem szólhatsz hozzá a bejegyzésekhez.'
                  : 'Ehhez a bejegyzéshez nem lehet hozzászólni.'}
            </p>
          )}
        </div>
      )}
    </article>
  );
};

export default BoardPostCard;
